import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { TextField, Alert } from "@mui/material";
import { toast } from "sonner";
import { toastApiError } from "@/api/client";
import { useTodayAttendance, useMarkOut } from "@/api/hooks/attendance";
import { useMe } from "@/api/hooks/auth";
import { useGeolocation } from "../hooks/useGeolocation";
import { LogOut } from "@/shared/icons";
import { SlideToConfirm } from "@/shared/components/SlideToConfirm";
import {
  asGeofenceError,
  asGeofenceAmbiguousError,
  haversineM,
  type AmbiguousCandidate,
  type GeofenceErrorDetails,
} from "../utils/geofenceError";
import { LocationPickerDialog } from "./LocationPickerDialog";
import { formatTime } from "@/shared/utils/format";
import { GeofenceLiveMap, type FencePoint } from "./GeofenceLiveMap";

/**
 * Logout-time attendance prompt — UI ported from the wm-modal* design.
 * Three exits:
 *   1. Slide to mark out & sign out — captures geofence + mark-out, then
 *      caller signs out via onSignedOutAfterMarkOut.
 *   2. "Sign out only" — keeps attendance open (legacy "logout without
 *      mark-out").
 *   3. Cancel.
 */
export function LogoutAttendancePrompt({
  open,
  onCancel,
  onSignOutOnly,
  onSignedOutAfterMarkOut,
}: {
  open: boolean;
  onCancel: () => void;
  onSignOutOnly: () => void;
  onSignedOutAfterMarkOut: () => void;
}) {
  const att = useTodayAttendance(open);
  const attRow = att.data?.attendance ?? null;
  const me = useMe(open);
  const markOut = useMarkOut();
  const geo = useGeolocation();
  const myLocations = me.data?.locations ?? [];
  const fences: FencePoint[] = myLocations.map((loc) => ({
    lat: loc.lat,
    lng: loc.lng,
    radiusM: loc.radiusM,
    name: loc.name,
    isPrimary: loc.isPrimary,
  }));
  const markedInCoords =
    attRow?.markedInLat && attRow?.markedInLng
      ? { lat: Number(attRow.markedInLat), lng: Number(attRow.markedInLng), label: "Mark-in" }
      : null;

  const [overrideNote, setOverrideNote] = useState("");
  const [needOverride, setNeedOverride] = useState(false);
  const [geoDetails, setGeoDetails] = useState<GeofenceErrorDetails | null>(null);
  const [picker, setPicker] = useState<{
    coords: { lat: number; lng: number; accuracy?: number };
    candidates: AmbiguousCandidate[];
    resolver: { resolve: () => void; reject: (e: Error) => void } | null;
  } | null>(null);
  const [lastPickedLocationId, setLastPickedLocationId] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!open) return undefined;
    setOverrideNote("");
    setNeedOverride(false);
    setGeoDetails(null);
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [open]);

  // Geolocation is fetched only when the user actually slides to mark out.
  // No eager fetch — keeps Cancel / Sign-out-only / Request-leave clickable
  // even if the device is slow to resolve GPS.

  const elapsed = useMemo(() => {
    const at = attRow?.markedInAt ? new Date(attRow.markedInAt) : null;
    if (!at) return null;
    const ms = now.getTime() - at.getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    return `${h}h ${m.toString().padStart(2, "0")}m`;
  }, [attRow?.markedInAt, now]);

  const markInTime = useMemo(
    () => (attRow?.markedInAt ? formatTime(attRow.markedInAt) : null),
    [attRow?.markedInAt],
  );

  const runMarkOut = (
    coords: { lat: number; lng: number; accuracy?: number },
    locationId?: string,
  ) =>
    new Promise<void>((resolve, reject) => {
      markOut.mutate(
        {
          lat: coords.lat,
          lng: coords.lng,
          accuracyM: coords.accuracy,
          overrideNote: overrideNote.trim() || undefined,
          locationId,
        },
        {
          onSuccess: () => {
            toast.success("Marked out — done for today");
            setPicker(null);
            setLastPickedLocationId(null);
            onSignedOutAfterMarkOut();
            resolve();
          },
          onError: (e: unknown) => {
            const cands = asGeofenceAmbiguousError(e);
            if (cands && cands.length > 1) {
              setPicker({ coords, candidates: cands, resolver: null });
              reject(new Error("ambiguous"));
              return;
            }
            const gErr = asGeofenceError(e);
            if (gErr) {
              setNeedOverride(true);
              setGeoDetails(gErr);
              reject(new Error("override"));
              return;
            }
            toastApiError(e, "Mark-out failed");
            reject(new Error("submit"));
          },
        },
      );
    });

  const buildCandidates = (
    coords: { lat: number; lng: number },
  ): AmbiguousCandidate[] =>
    myLocations
      .map((loc) => {
        const d = haversineM(
          { lat: coords.lat, lng: coords.lng },
          { lat: loc.lat, lng: loc.lng },
        );
        return {
          locationId: loc.id,
          name: loc.name,
          distanceM: Math.round(d),
          radiusM: loc.radiusM,
          inside: d <= loc.radiusM,
          isPrimary: loc.isPrimary,
        } satisfies AmbiguousCandidate;
      })
      .sort((a, b) => a.distanceM - b.distanceM);

  const submitMarkOut = async () => {
    let coords;
    try {
      coords = await geo.fetch();
    } catch {
      throw new Error("geo");
    }
    if (myLocations.length > 1) {
      if (needOverride && lastPickedLocationId) {
        return runMarkOut(coords, lastPickedLocationId);
      }
      const candidates = buildCandidates(coords);
      return new Promise<void>((resolve, reject) => {
        setPicker({ coords, candidates, resolver: { resolve, reject } });
      });
    }
    return runMarkOut(coords);
  };

  const onPickConfirm = async (locationId: string) => {
    if (!picker) return;
    setLastPickedLocationId(locationId);
    try {
      await runMarkOut(picker.coords, locationId);
      picker.resolver?.resolve();
    } catch (e) {
      picker.resolver?.reject(e as Error);
    } finally {
      setPicker(null);
    }
  };

  const onPickCancel = () => {
    picker?.resolver?.reject(new Error("cancelled"));
    setPicker(null);
  };

  // `mutating` = the mark-out API call is in flight. Other dismiss actions
  // (Cancel / Sign out only / overlay click) gate on this only, NOT on
  // `geo.loading`, so a slow GPS fix doesn't trap the user in the modal.
  const mutating = markOut.isPending;
  const slideBusy = geo.loading || markOut.isPending;
  if (!open) return null;

  const liveClockText = (() => {
    const d = now;
    return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}:${d.getSeconds().toString().padStart(2, "0")}`;
  })();

  return createPortal(
    <div className="wm-modal-overlay" onClick={!mutating ? onCancel : undefined}>
      <div
        className="wm-modal"
        style={{ width: 600 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="wm-modal-header">
          <div className="wm-modal-icon" style={{ color: "var(--wm-danger)" }}>
            <svg
              viewBox="0 0 16 16"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M10 11l3-3-3-3M13 8H6M6 3H3v10h3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="wm-modal-title">Mark out & sign out</div>
            <div className="wm-modal-sub">
              You have an open attendance row. Mark out before leaving, or sign out only.
            </div>
          </div>
          <button
            type="button"
            className="wm-modal-x"
            onClick={onCancel}
            disabled={mutating}
            aria-label="Cancel"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="wm-modal-body">
          {markInTime && (
            <div className="wm-mo-summary">
              <div className="wm-mo-stat">
                <div className="l">Marked in</div>
                <div className="v mono">{markInTime}</div>
              </div>
              <div className="wm-mo-stat">
                <div className="l">Live clock</div>
                <div className="v mono">{liveClockText}</div>
              </div>
              <div className="wm-mo-stat">
                <div className="l">Elapsed</div>
                <div
                  className="v mono"
                  style={{ color: "var(--wm-ok)" }}
                >
                  {elapsed ?? "—"}
                </div>
              </div>
            </div>
          )}

          {/* Geofence preview + summary */}
          {attRow && (
            <div className="wm-mi-loc-stack">
              <GeofenceLiveMap
                fences={fences}
                user={
                  geo.lat != null && geo.lng != null
                    ? { lat: geo.lat, lng: geo.lng, accuracyM: geo.accuracy }
                    : null
                }
                marked={markedInCoords}
                within={
                  needOverride
                    ? false
                    : geoDetails == null
                      ? attRow.markedInWithinGeofence ?? null
                      : true
                }
                height={220}
              />
              <div className="wm-mi-loc-body">
                <div className="wm-mi-loc-row">
                  <span className="l">Mark-in location</span>
                  <span
                    className="v"
                    style={{
                      color:
                        attRow.markedInWithinGeofence === false
                          ? "var(--wm-danger)"
                          : "var(--wm-ok)",
                      fontWeight: 600,
                    }}
                  >
                    {attRow.markedInWithinGeofence === false ? "✕ Outside" : "✓ Within"}
                  </span>
                </div>
                <div className="wm-mi-loc-row">
                  <span className="l">Fence</span>
                  <span className="v">
                    {attRow.markedInGeofenceName ?? "—"}
                  </span>
                </div>
                <div className="wm-mi-loc-row">
                  <span className="l">Distance</span>
                  <span
                    className="v mono"
                    style={{
                      color:
                        attRow.markedInWithinGeofence === false
                          ? "var(--wm-danger)"
                          : "var(--wm-ok)",
                    }}
                  >
                    {attRow.markedInDistanceM != null
                      ? `${attRow.markedInDistanceM}m`
                      : "—"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {needOverride && (
            <div className="wm-att-banner warn">
              <strong>Outside assigned location.</strong>
              <span>
                {geoDetails?.closestLocationName
                  ? `${geoDetails.closestDistanceM ?? "?"}m from ${geoDetails.closestLocationName}${geoDetails.closestRadiusM ? ` (radius ${geoDetails.closestRadiusM}m)` : ""}.`
                  : "No assigned location matched."}{" "}
                Add a mark-out note ≥10 chars and slide again.
              </span>
            </div>
          )}

          <div>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                color: "var(--wm-ink-2)",
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {needOverride ? "Override note (required ≥10 chars)" : "End-of-day note (optional)"}
            </label>
            <TextField
              multiline
              minRows={2}
              fullWidth
              size="small"
              value={overrideNote}
              onChange={(e) => setOverrideNote(e.target.value)}
              placeholder="Reason for being outside, or summary for today…"
              error={needOverride && overrideNote.trim().length < 10}
            />
          </div>

          {geo.error && (
            <Alert severity="error" sx={{ fontSize: 12.5 }}>
              {geo.error}
            </Alert>
          )}
        </div>

        {/* Footer */}
        <div
          className="wm-modal-footer"
          style={{ flexDirection: "column", gap: 10, alignItems: "stretch" }}
        >
          <SlideToConfirm
            tone="danger"
            label={geo.loading ? "Locating…" : "Slide to mark out & sign out"}
            completedLabel={markOut.isPending ? "Marking out…" : "Marked out"}
            loading={slideBusy}
            disabled={needOverride && overrideNote.trim().length < 10}
            onConfirm={submitMarkOut}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 11, color: "var(--wm-ink-3)" }}>
              "Sign out only" keeps attendance open — mark out later from any device.
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="wm-btn sm"
                onClick={onCancel}
                disabled={mutating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="wm-btn sm"
                onClick={onSignOutOnly}
                disabled={mutating}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <LogOut size={12} /> Sign out only
              </button>
            </div>
          </div>
        </div>

        <LocationPickerDialog
          open={!!picker}
          candidates={picker?.candidates ?? []}
          title="Pick mark-out location"
          message={
            (picker?.candidates ?? []).some((c) => c.inside === false)
              ? "You aren't inside any assigned office. Pick which one to record this attendance against — you'll be asked for an override note."
              : "Choose which office to record this attendance against."
          }
          busy={markOut.isPending}
          onCancel={onPickCancel}
          onConfirm={onPickConfirm}
        />
      </div>
    </div>,
    document.body,
  );
}
